/** @jsxImportSource @emotion/react */
import { Box, Chip, Typography, useTheme } from '@mui/material'
import {
  dataGridContainer,
  riskTable,
  riskTableContainer,
  tableContainer,
} from './RiskTable.styles'
import { DataGrid, GridRenderCellParams, GridSortModel } from '@mui/x-data-grid'
import { useEffect, useMemo, useState } from 'react'
import { formatCurrency } from '@app/utils/numbers.utils'
import { roundForDisplay } from '@app/components/Molecules/Charts/utils/utils'
import { FancySuspense } from '@app/components/Atoms/FancySuspense'
import { AssetLayerDetails, useRiskTable } from './useRiskTable'
import { exposureComparator } from './RiskTableUtils'
import { RiskTableFilters } from './RiskTableFilters'
import { FeatureBanner } from '@app/components/Atoms/FeatureBanner/FeatureBanner'

export const RiskTable = () => {
  const theme = useTheme()

  const [sortModel, setSortModel] = useState<GridSortModel>([
    { field: 'exposure_value', sort: 'desc' },
  ])
  const [filters, setFilters] = useState<{
    elementType: string
    riskSource: string
    consequence: string
  }>({ elementType: '*', riskSource: '*', consequence: '*' })

  const { riskTableRows, zoomTo, gridApiRef, handleOnDataGridPageRender, filterOptions } =
    useRiskTable()

  const filteredRows = useMemo(() => {
    return riskTableRows.filter((row) => {
      if (filters.elementType !== '*' && row.assetLayerDetails.name !== filters.elementType)
        return false
      if (filters.riskSource !== '*' && row.hazardDisplayName !== filters.riskSource) return false
      if (
        filters.consequence !== '*' &&
        filterOptions.consequences[row.consequence]?.label !== filters.consequence
      )
        return false
      return true
    })
  }, [riskTableRows, filterOptions, filters])

  useEffect(() => {
    handleOnDataGridPageRender()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredRows, sortModel])

  const noData = (
    <Typography variant="inherit" fontStyle={'italic'}>
      No data
    </Typography>
  )

  return (
    <Box css={riskTableContainer()}>
      <FeatureBanner featureName="Asset Risk Table" />
      <Box css={riskTable({ theme })}>
        <Typography color="#0b2948" fontSize="16px" marginTop="8px" fontWeight="600">
          Risk Table
        </Typography>
        <Typography>
          The Risk Table shows all assets exposed to the selected hazard. Element panel filters do
          not affect the results shown here, and assets with no exposure are not included. Use the
          controls to filter the elements within the table. Select a row to see the element on the
          map.
        </Typography>
        <RiskTableFilters filters={filters} setFilters={setFilters} filterOptions={filterOptions} />
        <Box css={tableContainer({ theme })}>
          <DataGrid
            apiRef={gridApiRef}
            css={dataGridContainer({ theme })}
            sortingOrder={['desc', 'asc']}
            sortModel={sortModel}
            onSortModelChange={setSortModel}
            autoPageSize
            disableColumnFilter
            onRowClick={async (params) => {
              await zoomTo(
                params.row['assetType'],
                params.row['assetLayerDetails'],
                params.row['assetId'],
              )
            }}
            rowHeight={30}
            columnHeaderHeight={40}
            onPaginationModelChange={handleOnDataGridPageRender}
            columns={[
              {
                headerName: 'Asset ID',
                field: 'clientAssetId',
                renderCell: (params: GridRenderCellParams<any, string>) => {
                  if (params.value === '...')
                    return <FancySuspense isLoading={true} variant="inline" width={'70px'} />
                  return params.value == '-' ? noData : params.value
                },
              },
              {
                headerName: 'Element type',
                field: 'assetLayerDetails',
                sortComparator: (a: AssetLayerDetails, b: AssetLayerDetails) => {
                  return a.name.localeCompare(b.name)
                },
                width: 235,
                renderCell: (params: GridRenderCellParams<any, AssetLayerDetails>) =>
                  params.value?.name,
              },
              {
                headerName: 'Element name',
                field: 'elementName',
                width: 235,
                sortable: false,
                renderCell: (params: GridRenderCellParams<any, string>) => {
                  if (params.value === '...')
                    return <FancySuspense isLoading={true} variant="inline" width={'200px'} />
                  return params.value == '-' ? noData : params.value
                },
              },
              { headerName: 'Risk source', field: 'hazardDisplayName', width: 235 },
              {
                headerName: 'Consequence',
                field: 'consequence',
                width: 110,
                renderCell: (params: GridRenderCellParams<any, number>) => {
                  if (params.value === undefined) return noData
                  const consequenceDescription = filterOptions.consequences[params.value]
                  return (
                    <Chip
                      label={consequenceDescription.label}
                      size="small"
                      css={{
                        backgroundColor: consequenceDescription.bgColor,
                        color: consequenceDescription.fgColor,
                        height: '1.9em',
                        fontSize: '10px',
                        fontWeight: '700',
                      }}
                    />
                  )
                },
              },
              {
                headerName: 'Exposure',
                field: 'exposure_value',
                sortComparator: exposureComparator,
                renderCell: (params: GridRenderCellParams<any, string>) => {
                  const exposureValue = Number.parseFloat(params.value ?? '')

                  if (Number.isNaN(exposureValue)) return params.value
                  if (exposureValue === undefined) return noData
                  return `${roundForDisplay(exposureValue, params.row['unit'])}${params.row['unit']}`
                },
              },
              {
                headerName: 'Cost',
                field: 'cost',
                sortable: false,
                width: 70,
                renderCell: (params: GridRenderCellParams<any, number | string>) => {
                  if (params.value === '...')
                    return <FancySuspense isLoading={true} variant="inline" width={'35px'} />
                  if (typeof params.value === 'string') return params.value
                  if (!params.value) return noData
                  return formatCurrency(params.value)
                },
              },
            ]}
            rows={filteredRows}
          ></DataGrid>
        </Box>
      </Box>
    </Box>
  )
}
